import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { debounceTime, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { User } from '../../core/models/user.model';
import { environment } from '../../../environments/environment';
import { UserService } from '../../core/services/user.service';

@Injectable({
    providedIn: 'root'
})
export class IntercomService {
    private userInfo: any;
    private isInitialized: boolean = false;
    private readonly scriptId: string = 'intercom-widget-script';

    constructor(
        private readonly userService: UserService,
        private readonly router: Router,
    ) {}

    init(): Observable<void> {
        if (this.isInitialized) {
            return of(undefined);
        }
        this.isInitialized = true;
        this.boot();
        this.startIntercomListener();
        return of(undefined);
    }

    setupIntercomStub(): void {
        if (typeof window === 'undefined') return;
        // @ts-ignore
        if (window.Intercom && typeof window.Intercom === 'function') return;

        // @ts-ignore
        window.intercomSettings = { app_id: environment.intercomAppId };

        const i: any = function(...args: any[]) { i.c(args); };
        i.q = [];
        i.c = function(args: any[]) { i.q.push(args); };
        // @ts-ignore
        window.Intercom = i;
    }

    shutDown(): void {
        this.userInfo = undefined;
        // @ts-ignore
        window.Intercom?.('shutdown');
        this.removeIntercomScript();
    }

    private boot(): void {
        this.userService.getUserData().pipe(
          filter(Boolean),
          take(1),
        ).subscribe((user: User) => {
            this.userInfo = this.getUserInfo(user);
            // @ts-ignore
            window.Intercom('boot', this.userInfo);
            this.loadIntercomScript();
        })
    }

    private startIntercomListener(): void {
        this.router.events.pipe(
          filter((event: Event) => event instanceof NavigationEnd),
          map((event: Event) => event?.['urlAfterRedirects']),
          withLatestFrom(this.userService.getUserData()),
        )
        .pipe(debounceTime(500))
        .subscribe(([url, user]: [string, User]) => {
            if (this.userInfo && user) {
                const updatedUserInfo = this.getUpdatedUserInfo(user);
                this.userInfo = {
                    ...this.userInfo,
                    ...updatedUserInfo
                };
                // @ts-ignore
                window.Intercom('update', updatedUserInfo);
                return;
            }
        });
    }

    private loadIntercomScript(): void {
        if (typeof window === 'undefined') return;
        if (document.getElementById(this.scriptId)) {
            return;
        }

        const script = document.createElement('script');
        script.id = this.scriptId;
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${environment.intercomAppId}`;
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.head.appendChild(script);
        }
    }

    private removeIntercomScript(): void {
        if (typeof window === 'undefined') return;
        const script = document.getElementById(this.scriptId);
        if (script?.parentNode) {
            script.parentNode.removeChild(script);
        }
    }

    private getUserInfo(user: User) {
        const account_creation_date: Date = new Date(user.account_creation_date);
        const creation_date = new Date(user.creation_date);
        const billing_period_start = new Date(user.billing_period_start);
        const billing_period_end = new Date(user.billing_period_end);
        return {
            accounttype: user.account_type?.toString(),
            app_id: environment.intercomAppId,
            account_creation_date: account_creation_date.getTime() / 1000,
            billing_start_at: billing_period_start.getTime() / 1000,
            billing_end_at: billing_period_end.getTime() / 1000,
            created_at: creation_date.getTime() / 1000,
            user_creation_date: creation_date.getTime() / 1000,
            company: {
                name: user.account_name,
                company_id: user.account_id.toString(),
                adsintel: user.ads_intel_enabled,
                prospectconnect: user.prospect_connect_enabled,
                SSO: user.sso_enabled,
            },
            email: user.email,
            is_admin: user.is_admin,
            is_ads_intel_user: user.is_ads_intel_user,
            is_prospectconnect_user: user.is_prospect_connect_user,
            is_autorenew: user.is_autorenew,
            is_chrome_only: user.is_chrome_only,
            job_change_enabled: user.job_change_enabled,
            job_change_freemium: user.job_change_freemium,
            name: [user.first_name, user.last_name].join(' '),
            revdriver_version: user.rev_driver_version || null,
            user_id: user.id,
            user_limit: user.user_limit
        };
    }

    private getUpdatedUserInfo(user: User) {
        const updatedUser = this.getUserInfo(user);
        const updatedUserKeys = Object.keys(updatedUser).reduce((prev: Record<string, any>, key: string) => {
            return {
                ...prev,
                ...(updatedUser[key] !== this.userInfo[key] ? { [key]: updatedUser[key] } : {})
            };
        }, {});
        return {
            app_id: environment.intercomAppId,
            email: user.email,
            user_id: user.id,
            ...updatedUserKeys,
        };
    }
}
